import React, { FC, memo, useEffect, useMemo, useState } from 'react'

import { useFocusNodeById, useSetFocus } from '@please/lrud'
import { fromUnixTime } from 'date-fns'
import { Trans } from 'react-i18next'

import { capitalizeFirstLetter } from '@sportworld/js-sdk/src/utils/utils'

import { updateCreditWallets } from '@/actions/creditWallets'
import { Loader } from '@/components/Loader'
import { PPVActions } from '@/components/PPVPopup/PPVActions'
import { PPVEventInfo } from '@/components/PPVPopup/PPVEventInfo'
import { SelectPPVCredits } from '@/components/PPVPopup/SelectPPVCredits'
import { eventStatus } from '@/constants/common'
import { focusIds, orientation } from '@/constants/focus'
import { icons } from '@/constants/icons'
import { useEventStatus } from '@/containers/EventDetailPage/hooks'
import { getCurrentLanguage } from '@/i18n'
import { dateTranslation } from '@/i18n/dateTranslation'
import { useCommonTranslation } from '@/i18n/useCommonTranslation'
import { usePPVPopupStore } from '@/store/usePPVPopupStore'
import useStore from '@/store/useStore'
import {
  eventTypes,
  PayPerViewInfoType,
} from '@/types/servicesTypes/configTypes'
import { getTimeFormat } from '@/utils/utils'

import {
  StatusBox,
  LoaderWrapper,
  PpvContainer,
  PpvPopup,
  PurchaseInfo,
  PurchaseInfoBlock,
  PurchaseText,
  PurchaseTextWrapper,
  // QrCode,
  Title,
  StatusIcon,
  StatusLabel,
  Note,
  WarningText,
} from './styles'

export type AvailablePPVWallet = PayPerViewInfoType & {
  actualCredits: number
  remainingCredits: number
  creditsName: string
}

export enum PurchaseStatus {
  PROCESS = 'PurchaseProcess',
  SUCCESS = 'PurchaseSuccess',
  FAILED = 'PurchaseFailed',
}

const PPVPopupRaw: FC = () => {
  const setFocus = useSetFocus()
  const { t } = useCommonTranslation()
  const { ppvPopup, purchaseStatus, setPurchaseStatus } = usePPVPopupStore(
    (state) => state,
  )
  const userCreditsWallets = useStore(
    (state) => state.common.userCreditsWallets,
  )
  const [activePPVWallet, setActivePPVWallet] = useState<AvailablePPVWallet>()
  const [isWarningScreen, setIsWarningScreen] = useState<boolean>(false)

  const currentEventStatus = useEventStatus(ppvPopup?.event || null)
  const focusNode = useFocusNodeById(focusIds.PPV_POPUP)

  const ppvInfo = ppvPopup?.event?.ppvInfo
  const isPurchaseProcess = purchaseStatus === PurchaseStatus.PROCESS
  const isPurchaseSuccess = purchaseStatus === PurchaseStatus.SUCCESS
  const isPurchaseFailed = purchaseStatus === PurchaseStatus.FAILED
  const isPurchaseCompleted = isPurchaseSuccess || isPurchaseFailed

  const isChannelEvent = ppvPopup?.event.type === eventTypes.CHANNEL
  const isFutureEvent = currentEventStatus === eventStatus.IS_FUTURE
  const isFutureChannelEvent = isChannelEvent && isFutureEvent
  const isLiveNow = isChannelEvent && currentEventStatus === eventStatus.IS_LIVE
  const isPastChannelEvent =
    isChannelEvent && currentEventStatus === eventStatus.IS_PAST

  const isPayablePPVWallet =
    !!activePPVWallet && activePPVWallet.remainingCredits >= 0

  const availablePPVWallets = useMemo(() => {
    if (!ppvInfo) {
      return []
    }

    return ppvInfo.map((ppvWallet: any) => {
      const wallet = userCreditsWallets?.find(
        (creditWallet) => creditWallet.uniqueId === ppvWallet.walletId,
      )
      const actualCredits = wallet?.availableCreditCount || 0
      const requiredCredits = ppvWallet.cost
      const creditsName = capitalizeFirstLetter(
        ppvWallet.walletId?.replace(/^promotion-/, '').replace(/-credits$/, ''),
      )

      return {
        ...ppvWallet,
        actualCredits,
        remainingCredits: actualCredits - requiredCredits,
        creditsName,
      }
    }, [])
  }, [ppvInfo, userCreditsWallets])

  const getDefaultPPVWallet = (availablePPVWallets: AvailablePPVWallet[]) => {
    const defaultWalletId = availablePPVWallets.find(
      (ppv) => ppv.remainingCredits >= 0,
    )
    setActivePPVWallet(defaultWalletId ?? availablePPVWallets[0])
  }

  useEffect(() => {
    if (!availablePPVWallets.length) {
      return setActivePPVWallet(undefined)
    }
    getDefaultPPVWallet(availablePPVWallets)
  }, [availablePPVWallets])

  useEffect(() => {
    focusNode && setTimeout(setFocus, 0, focusIds.PPV_POPUP)
  }, [!!focusNode])

  useEffect(() => {
    if (!ppvPopup) {
      setIsWarningScreen(false)
      return setPurchaseStatus(null)
    }
    updateCreditWallets()
  }, [ppvPopup])

  if (!ppvPopup) {
    return null
  }

  const timeFormat = getTimeFormat()

  const renderContent = () => {
    if (isWarningScreen) {
      const name = ppvPopup.event.name
      const startTime = dateTranslation(
        fromUnixTime(ppvPopup.event.startTime),
        timeFormat,
      )
      const endTime = dateTranslation(
        fromUnixTime(ppvPopup.event.endTime),
        timeFormat,
      )

      return (
        <StatusBox>
          <Title>
            {isPastChannelEvent
              ? t('eventOver')
              : t('ppvPopup.eventAlreadyRunning')}
          </Title>
          {!isPastChannelEvent && (
            <WarningText>
              <Trans
                i18nKey={'ppvPopup.warningInfo'}
                values={{ name, startTime, endTime }}
              />
            </WarningText>
          )}
          <WarningText>{t('ppvPopup.warningInfo2')}</WarningText>
        </StatusBox>
      )
    }
    const creditsName = activePPVWallet?.creditsName
    const language = getCurrentLanguage()
    return (
      <>
        <Title>{t('ppvPopup.orderOverview')}</Title>
        <PPVEventInfo currentEventStatus={currentEventStatus} />
        {activePPVWallet && (
          <PurchaseInfo>
            {availablePPVWallets.length === 1 && (
              <PurchaseInfoBlock>
                {t('ppvPopup.actualCredits', { creditsName })}
                <span>
                  {activePPVWallet?.actualCredits?.toLocaleString(language)}
                </span>
              </PurchaseInfoBlock>
            )}
            <PurchaseInfoBlock $accent={true}>
              {t('ppvPopup.requiredCredits', { creditsName })}
              <span>{activePPVWallet?.cost?.toLocaleString(language)}</span>
            </PurchaseInfoBlock>
            {availablePPVWallets.length === 1 && isPayablePPVWallet && (
              <PurchaseInfoBlock>
                {t('ppvPopup.creditsLeftAfterPurchase', { creditsName })}
                <span>
                  {activePPVWallet?.remainingCredits?.toLocaleString(language)}
                </span>
              </PurchaseInfoBlock>
            )}
          </PurchaseInfo>
        )}
        {availablePPVWallets.length > 1 && (
          <SelectPPVCredits
            availablePPVWallets={availablePPVWallets}
            activePPVWallet={activePPVWallet}
            setActivePPVWallet={setActivePPVWallet}
          />
        )}
        {isPayablePPVWallet ? (
          <PurchaseText>{t('ppvPopup.agreement')}</PurchaseText>
        ) : (
          <PurchaseTextWrapper>
            <PurchaseText>{t('ppvPopup.isNotEnoughCredits')}</PurchaseText>
            {/* <QrCode src={icons.navigation.exit} alt="" /> */}
          </PurchaseTextWrapper>
        )}
      </>
    )
  }

  const renderCompletedScreen = () => {
    const icon = isPurchaseSuccess
      ? icons.register.successIcon
      : icons.register.failIcon
    const label = isPurchaseSuccess
      ? t('ppvPopup.successfulPurchase')
      : t('ppvPopup.redeemFailed')
    return (
      <StatusBox>
        <StatusIcon>
          <img src={icon} alt="" />
        </StatusIcon>
        <StatusLabel>{label}</StatusLabel>
        {isPurchaseFailed && <Note>{t('ppvPopup.redeemFailedInfo')}</Note>}
      </StatusBox>
    )
  }

  return (
    <PpvPopup
      orientation={orientation.VERTICAL}
      focusId={focusIds.PPV_POPUP}
      isTrap={true}
    >
      <PpvContainer>
        {isPurchaseCompleted ? renderCompletedScreen() : renderContent()}
        <PPVActions
          isPayablePPVWallet={isPayablePPVWallet}
          activePPVWallet={activePPVWallet}
          purchaseItemId={ppvPopup.event.id}
          isFutureChannelEvent={isFutureChannelEvent}
          isLiveNow={isLiveNow}
          isWarningScreen={isWarningScreen}
          setIsWarningScreen={setIsWarningScreen}
        />
        {isPurchaseProcess && (
          <LoaderWrapper>
            <Loader />
          </LoaderWrapper>
        )}
      </PpvContainer>
    </PpvPopup>
  )
}

export const PPVPopup = memo(PPVPopupRaw)
